import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivationEnd, Event, Router } from '@angular/router';
import { FeatureFlags } from '@core/enumeration';
import {
  AuthenticationService,
  BroadcastAppService,
  BroadcastDocumentsService,
  BroadcastServicelinesService,
  TenantService
} from '@core/service';
import { MenuItems } from '@shared/enumeration';
import { FeatureToggleService, SecurityService, Tenant, TenantNames } from '@mynexus/mynexus-ui-lib';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubSink } from 'subsink';
import { ShellService } from '@mynexus/mfe-core';

interface IMenu {
  path: string;
  showMember?: boolean;
  showFullSideNav?: boolean;
  menu: string;
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-sidebar',
    templateUrl: 'sidebar.component.html',
    styleUrls: ['sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
  selectedTenant: Tenant = null;
  activeTenantId: string;
  tenantName: string;
  showFullSideNav = false;
  showMember = false;
  menu = 'queues';
  private subs = new SubSink();
  private readonly destroyRef = inject(DestroyRef);
  isExternalUser = true;
  isWoundManagementEnabled = false;
  isAuthEligible = false;
  pendingRequestsDashboardAccessable = false;
  isHHAutomationEnabled = false;

  readonly TenantEmptyValue = 'none';

  private readonly menuMap: IMenu[] = [
    {
      path: '/auth-dashboard',
      showMember: false,
      showFullSideNav: true,
      menu: MenuItems.AUTHORIZATIONS_ACTIVE,
    },
    {
      path: '/member/',
      showMember: true,
      showFullSideNav: false,
      menu: MenuItems.MEMBER,
    },
    {
      path: '/AdminPanel',
      menu: MenuItems.ADMIN_PANEL,
    },
    {
      path: '/FaxHistory',
      menu: MenuItems.FAX_HISTORY,
    },
    {
      path: '/InboundFaxes',
      menu: MenuItems.INBOUND_FAXES,
    },
    {
      path: '/advanced-search',
      menu: MenuItems.ADVANCED_SEARCH,
    },
    {
      path: '/AuthorizationsViewList',
      menu: MenuItems.AUTHORIZATION_HISTORY,
    },
    {
      path: '/home',
      menu: MenuItems.QUEUES,
    },
    {
      path: '/activity-center',
      menu: MenuItems.ACTIVITY_CENTER,
    },
    {
      path: '/request-initial',
      menu: MenuItems.REQUEST,
    },
    {
      path: '/request-subsequent',
      menu: MenuItems.REQUEST,
    },
    {
      path: '/provider-dashboard',
      menu: 'pac-provider-dashboard'
    },
    {
      path: '/pending-requests-dashboard',
      menu: 'pending-requests-dashboard'
    }
  ];

  get isAnthem(): boolean {
    return this.tenantName === TenantNames.Anthem;
  }

  get providerDashboardEnabled(): boolean {
    const correctUser = this.securityService.hasAccess(['ProviderDashboard.Read']) && this.isExternalUser;
    const notEmptyTenant = this.activeTenantId !== this.TenantEmptyValue;

    return notEmptyTenant && correctUser;
  }

  get pendingRequestsDashboardEnabled(): boolean {
    return this.securityService.hasAccess(['PendingRequest.Read'])
      && this.pendingRequestsDashboardAccessable
      && this.isExternalUser
      && this.activeTenantId !== this.TenantEmptyValue
      && this.isAnthem;
  }

  get queuesEnabled(): boolean {
      const queuePermissions = [
        'AppealGrievanceQueue.Read',
        'DMEPOSMedicaidQueue.Read',
        'DMEPOSAuthorizationCaseQueue.Read',
        'HHServiceLineQueue.Read',
        'PACCOMServiceLineQueue.Read',
        'PACMAServiceLineQueue.Read',
        'SDoHQueue.Read',
        'WoundCareAdmissionQueue.Read'
      ];

      const hasQueueAccess = this.securityService.hasAccess(queuePermissions) && !this.isExternalUser;
      return hasQueueAccess;
  }

  get showHHButtons(): boolean {
    return !this.showPacButtons && !this.showDmeposButtons;
  }

  get showPacMAButtons(): boolean {
    return this.router.url.includes('pac-im');
  }

  get showPacButtons(): boolean {
    return this.showPacMAButtons || this.router.url.includes('pac-commercial');
  }

  get showDmeposButtons(): boolean {
    return this.router.url.includes('dmepos');
  }

  constructor(
    private readonly ref: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private broadcastAppService: BroadcastAppService,
    private broadcastServiceLinesService: BroadcastServicelinesService,
    private broadcastDocumentsService: BroadcastDocumentsService,
    private securityService: SecurityService,
    private featureToggleService: FeatureToggleService,
    private tenantService: TenantService,
    private shellService: ShellService
  ) {
    this.isExternalUser = this.authenticationService.isExternalUser;
  }

  ngOnInit(): void {
    this.activeMenuInit();
    this.setMenu();

    if (this.activatedRoute.firstChild) {
      this.subs.sink = this.activatedRoute.firstChild.params.subscribe(params => {
        if (!this.activeTenantId) {
          this.activeTenantId = params.tenantId;
          this.setTenantName();
        }
        this.ref.markForCheck();
      });
    }

    this.subs.sink = this.router.events.subscribe((event: Event) => {
      if (!this.activeTenantId && event instanceof ActivationEnd) {
        this.activeTenantId = event.snapshot ? event.snapshot.params.tenantId : null;
      }
      this.setMenu();
      this.ref.markForCheck();
    });

    this.subs.sink = this.broadcastAppService.healthPlan.subscribe((tenantId: string) => {
      if (!this.activeTenantId || tenantId !== this.TenantEmptyValue) {
        this.activeTenantId = tenantId;
        this.setTenantName();
      }
    });

    // Capture feature flags here
    this.subs.sink = combineLatest({
      woundManagementFlag: this.featureToggleService.getFeatureAccess(FeatureFlags.WoundManagement),
    }).subscribe(({woundManagementFlag}) => {
      this.isWoundManagementEnabled = woundManagementFlag;

      //Run change detection
      this.ref.markForCheck();
    });

    this.subs.sink = this.broadcastAppService.subscribe('authorization:data', ({ payload }) => {
      this.isAuthEligible = payload.data?.isAuthEligible;
      this.isHHAutomationEnabled = payload.data?.isHHAutomationEnabled;
      this.ref.markForCheck();
    });

    this.getPendingRequestsDashboardAccess();
  }

  activeMenuInit() {
    this.subs.sink = this.broadcastAppService.subscribe('animate:menu', payload => {
      this.menu = payload.payload.menu;
      this.ref.markForCheck();
    });
    const href = this.router.url;
    if (href.includes('AuthorizationsViewList')) {
      this.menu = MenuItems.AUTHORIZATION_HISTORY;
    } else if (href.includes('FaxHistory')) {
      this.menu = MenuItems.FAX_HISTORY;
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  setMenu() {
    const activeMenuItem = this.menuMap.find(item => this.router.url.includes(item.path));

    if (activeMenuItem) {
      if (activeMenuItem.path === '/auth-dashboard' || activeMenuItem.path === '/member/') {
        this.showMember = activeMenuItem.showMember;
        this.showFullSideNav = activeMenuItem.showFullSideNav;

      } else {
        this.resetMenu();
      }
      this.menu = activeMenuItem.menu;
    } else {
      this.resetMenuState();
    }
  }

  resetMenu() {
    this.showMember = false;
    this.showFullSideNav = false;
  }

  goToPage(urlPart: string) {
    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.resetNotesSidebar();
            this.gotoMenu(urlPart);
            this.resetActiveMenu();
          }
        });
      } else {
        this.gotoMenu(urlPart);
        this.resetNotesSidebar();
        this.resetActiveMenu();
      }
      return;
    }
    
    this.resetNotesSidebar();
    this.gotoMenu(urlPart);
    this.resetActiveMenu();
  }

  goToPageWithTenantId(urlPart: string) {
    const tenantId = this.activeTenantId ? this.activeTenantId : this.TenantEmptyValue;
    const fullUrl = `/${tenantId}/${urlPart}`;
    if (!this.broadcastAppService.isNavigationBlocked) {
      this.resetNotesSidebar();
    }
    this.gotoMenu(fullUrl);
    this.resetActiveMenu();
  }

  goToPacProviderDashboard(): void {
    this.goToPage(`/${this.activeTenantId}/provider-dashboard`);
  }

  gotoAdminPanel() {
    this.gotoMenu('/AdminPanel');
  }

  // add to general class?
  gotoMenu(route: string) {
    this.router.navigate([route]);
  }

  goToPacPendingRequestsDashboard(): void {
    this.goToPage(`/${this.activeTenantId}/pending-requests-dashboard`);
  }
  
  activeMenuClick(type: string) {
    if (this.broadcastAppService.isNavigationBlocked) {
      const noteMfeService = this.shellService.getMfeService('mnc-notes') as any;
      if (noteMfeService && typeof noteMfeService.requestDeactivationConfirm === 'function') {
        noteMfeService.requestDeactivationConfirm().subscribe((canNavigate: boolean) => {
          if (canNavigate) {
            this.resetNotesSidebar();
            this.menu = type;
            this.broadcastAppService.broadcast('animate:menu', {
              animation: 'flash',
              menu: type
            });
            this.resetActiveMenu();
            this.ref.markForCheck();
          }
        });
      } else {
        this.resetNotesSidebar();
        this.menu = type;
        this.broadcastAppService.broadcast('animate:menu', {
          animation: 'flash',
          menu: type
        });
        this.resetActiveMenu();
      }
      return;
    }
    this.resetNotesSidebar();
    this.menu = type;
    this.broadcastAppService.broadcast('animate:menu', {
      animation: 'flash',
      menu: type
    });
    this.resetActiveMenu();
  }

  resetNotesSidebar() {
    this.broadcastAppService.broadcastNotes(false, undefined, undefined);
    this.broadcastServiceLinesService.broadcastServiceLinesData(false, null, []);
    this.broadcastServiceLinesService.broadcastServiceLinesDataHH(false, null, []);
    this.broadcastDocumentsService.broadcastDocumentsData(false, null, []);
  }

  resetActiveMenu() {
    this.broadcastAppService.broadcast(null, null);
  }

  /**
   * Deselect menu item and reset menu tree to initial state
   */
  private resetMenuState(): void {
    this.menu = null;
    this.resetMenu();
  }

  private getPendingRequestsDashboardAccess(): void {
    this.subs.sink = this.featureToggleService.getFeatureAccess(FeatureFlags.PACPendingRequestDashboard).subscribe(enabled => {
      this.pendingRequestsDashboardAccessable = enabled;
    });
  }

  private setTenantName(): void {
    if (!this.activeTenantId || this.activeTenantId === this.TenantEmptyValue) return;

    this.subs.sink = this.tenantService.getTenants(this.isExternalUser).subscribe(res => {
      const tenant = res.find(el => el.tenantId === this.activeTenantId);
      if (tenant) {
        this.tenantName = tenant.name;
      }
    });
  }
}
